import { useTranslation } from "react-i18next";
import { Select, Stack } from "@mantine/core";
import { QuestionConditionType, QuestionType } from "src/graphql/generated/graphql";

import { useQuestionnaireFormContext } from "../formContext";
import { ConditionValue } from "./conditionValue";

const allowedDependsOnQuestionTypes = [
    QuestionType.Radio,
    QuestionType.Checkbox,
    QuestionType.Number,
] as QuestionType[];

const dependsOnConditionTypes: Partial<Record<QuestionType, QuestionConditionType[]>> = {
    [QuestionType.Radio]: [QuestionConditionType.None, QuestionConditionType.Oneof, QuestionConditionType.Eq],
    [QuestionType.Checkbox]: [QuestionConditionType.None, QuestionConditionType.Oneof, QuestionConditionType.Eq],
    [QuestionType.Number]: [
        QuestionConditionType.None,
        QuestionConditionType.Oneof,
        QuestionConditionType.Eq,
        QuestionConditionType.Lte,
        QuestionConditionType.Gte,
    ],
};

export interface ConditionProps {
    questionIndex: number;
}

export const Condition = ({ questionIndex }: ConditionProps) => {
    const { t } = useTranslation(["common"]);

    const questionnaireForm = useQuestionnaireFormContext();

    const question = questionnaireForm.values?.questions?.[questionIndex];

    const dependsOn = question?.condition?.dependsOn
        ? (questionnaireForm.values?.questions || []).find((item) => item?.key === question.condition.dependsOn)
        : null;

    return (
        <Stack spacing="xs">
            <Select
                {...questionnaireForm.getInputProps(`questions.${questionIndex}.condition.dependsOn`)}
                label={t("questionnaire_question_condition_dependson")}
                placeholder={t("questionnaire_question_condition_dependson")}
                data={(questionnaireForm.values?.questions || [])
                    .filter((question, index) => {
                        return index !== questionIndex && allowedDependsOnQuestionTypes.indexOf(question?.type) >= 0;
                    })
                    .map((question) => ({
                        value: question?.key,
                        label: question?.text,
                    }))}
            />
            <Select
                {...questionnaireForm.getInputProps(`questions.${questionIndex}.condition.type`)}
                label={t("questionnaire_question_condition_type")}
                placeholder={t("questionnaire_question_condition_type")}
                disabled={!dependsOn?.key}
                data={(dependsOnConditionTypes[dependsOn?.type] || []).map((value) => ({
                    value,
                    label: t(`questionnaire_question_condition_type_${value}`),
                }))}
            />
            <ConditionValue questionIndex={questionIndex} dependsOn={dependsOn} />
        </Stack>
    );
};
